import { useState } from 'react'
import { router } from '@inertiajs/react'
import type CrmCoreUser from '@/types/serializers/CrmCoreUser'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Star, X } from 'lucide-react'

interface TeamMembershipsManagerProps {
  user: CrmCoreUser
  teams: { id: number; name: string }[]
}

export function TeamMembershipsManager({ user, teams }: TeamMembershipsManagerProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  const memberTeamIds = new Set(user.memberships.map((m) => m.team_id))
  const availableTeams = teams.filter((t) => !memberTeamIds.has(t.id))

  function handleError(errors: Record<string, string>) {
    setError(errors['membership.base'] ?? Object.values(errors)[0] ?? 'Something went wrong.')
  }

  function handleAdd() {
    if (!selectedTeamId) return
    setError(null)
    setProcessing(true)
    router.post(
      `/admin/users/${user.id}/add_membership`,
      { team_id: parseInt(selectedTeamId, 10) },
      {
        preserveScroll: true,
        onSuccess: () => setSelectedTeamId(''),
        onError: (errors) => handleError(errors as unknown as Record<string, string>),
        onFinish: () => setProcessing(false),
      },
    )
  }

  function handleRemove(teamId: number) {
    setError(null)
    setProcessing(true)
    router.delete(`/admin/users/${user.id}/remove_membership`, {
      data: { team_id: teamId },
      preserveScroll: true,
      onError: (errors) => handleError(errors as unknown as Record<string, string>),
      onFinish: () => setProcessing(false),
    })
  }

  function handleSetPrimary(teamId: number) {
    setError(null)
    setProcessing(true)
    router.patch(
      `/admin/users/${user.id}/set_primary`,
      { team_id: teamId },
      {
        preserveScroll: true,
        onError: (errors) => handleError(errors as unknown as Record<string, string>),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-muted-foreground">Teams</p>

      {user.memberships.length === 0 && (
        <p className="text-sm text-muted-foreground">No team memberships.</p>
      )}

      <ul className="space-y-2">
        {user.memberships.map((membership) => (
          <li
            key={membership.team_id}
            className="flex items-center justify-between gap-2 rounded-md border p-2"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm">{membership.team_name}</span>
              {membership.primary && (
                <Badge variant="secondary">
                  <Star className="mr-1 h-3 w-3" />
                  Primary
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {!membership.primary && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={processing}
                  onClick={() => handleSetPrimary(membership.team_id)}
                >
                  Set primary
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                disabled={processing}
                onClick={() => handleRemove(membership.team_id)}
                aria-label={`Remove ${membership.team_name}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {availableTeams.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Add a team..." />
            </SelectTrigger>
            <SelectContent>
              {availableTeams.map((team) => (
                <SelectItem key={team.id} value={String(team.id)}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleAdd} disabled={!selectedTeamId || processing}>
            Add
          </Button>
        </div>
      )}
    </div>
  )
}
